/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import {
  type TherapistDocumentType,
  type UploadTherapistDocumentInput,
} from '@therapy/shared-validators';

import { apiClient } from './client.js';

export interface TherapistDocument {
  readonly id: string;
  readonly docType: TherapistDocumentType;
  readonly title: string;
  readonly fileName: string;
  readonly contentType: string;
  readonly sizeBytes: number;
  readonly version: number;
  readonly supersededAt: string | null;
  readonly uploadedAt: string;
}

/** Stream an authenticated file response to the browser as a download. */
async function downloadTo(path: string, fileName: string): Promise<void> {
  const { data } = await apiClient.get<Blob>(path, { responseType: 'blob' });
  const url = URL.createObjectURL(data);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export const therapistDocumentsApi = {
  async list(therapistId: string): Promise<readonly TherapistDocument[]> {
    const { data } = await apiClient.get<readonly TherapistDocument[]>(
      `/therapists/${therapistId}/documents`,
    );
    return data;
  },
  async listMine(): Promise<readonly TherapistDocument[]> {
    const { data } = await apiClient.get<readonly TherapistDocument[]>('/therapist-documents/mine');
    return data;
  },
  async upload(
    therapistId: string,
    input: UploadTherapistDocumentInput,
  ): Promise<TherapistDocument> {
    const { data } = await apiClient.post<TherapistDocument>(
      `/therapists/${therapistId}/documents`,
      input,
    );
    return data;
  },
  async remove(therapistId: string, docId: string): Promise<void> {
    await apiClient.delete(`/therapists/${therapistId}/documents/${docId}`);
  },
  async download(therapistId: string, doc: TherapistDocument): Promise<void> {
    await downloadTo(`/therapists/${therapistId}/documents/${doc.id}/download`, doc.fileName);
  },
  async downloadMine(doc: TherapistDocument): Promise<void> {
    await downloadTo(`/therapist-documents/${doc.id}/download`, doc.fileName);
  },
};
